import React, { useState } from 'react';
import { X, Loader2, Wand2, Copy, FileText, ExternalLink, Download } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';

interface SavedOutput {
  id: string;
  saved: boolean;
}

interface MethodologyAgentProps {
  onClose: () => void;
}

export default function MethodologyAgent({ onClose }: MethodologyAgentProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [savedOutput, setSavedOutput] = useState<SavedOutput | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please enter project details');
      return;
    }

    setLoading(true);
    try {
      const prompt = `As a project methodology expert, analyze the following project details and recommend the most suitable project management methodology. Consider these methodologies:

1. Agile/Scrum - For projects with evolving requirements, high stakeholder involvement, and flexible timeline
2. Waterfall - For projects with clear scope, fixed budget, and strict deadlines
3. Kanban - For ongoing projects with continuous improvements
4. Hybrid - For projects needing a mix of Agile and Waterfall approaches

Project Details:
${input}

Please provide a detailed analysis in the following format:

1. Project Analysis
   - Extract and analyze key project characteristics
   - Identify critical success factors
   - List potential challenges

2. Methodology Recommendation
   - Recommended methodology with clear justification
   - Why this methodology fits the project
   - How it addresses project challenges

3. Implementation Strategy
   - Step-by-step implementation plan
   - Key roles and responsibilities
   - Required tools and processes

4. Risk Mitigation
   - Potential risks with chosen methodology
   - Mitigation strategies
   - Success metrics to track

Use HTML formatting for better readability. Focus on practical, actionable recommendations.`;

      const response = await generateTaskDescription(prompt, 'task');
      setResult(response);
      
      // Prepare for saving but don't save automatically
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating methodology recommendation:', error);
      toast.error('Failed to generate recommendation');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }
      
      // First try to find the agent by ID
      const { data: existingAgent, error: fetchError } = await supabase
        .from('ai_agents')
        .select('id')
        .eq('id', '00000000-0000-0000-0000-000000000001')
        .limit(1)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error fetching agent:', fetchError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      let agentId: string;
      
      if (!existingAgent) {
        // Try to find by name
        const { data: agentByName, error: nameError } = await supabase
          .from('ai_agents')
          .select('id')
          .eq('name', 'Project Methodology Advisor')
          .limit(1)
          .maybeSingle();
          
        if (nameError && nameError.code !== 'PGRST116') {
          console.error('Error finding agent by name:', nameError);
          toast.error('Failed to save output');
          setSavingOutput(false);
          return;
        }
        
        if (agentByName) {
          agentId = agentByName.id;
        } else {
          // Create new agent
          const { data: newAgent, error: createError } = await supabase
          .from('ai_agents')
          .insert({
            name: 'Project Methodology Advisor',
            user_id: user.id,
            goals: 'Recommend suitable project management methodologies',
            system_prompt: 'Expert in project management methodologies',
            is_custom: false,
            agent_type: 'methodology'
          })
          .select('id')
          .single();
          
          if (createError) {
            console.error('Error creating agent:', createError);
            toast.error('Failed to create agent');
            setSavingOutput(false);
            return;
          }
          
          agentId = newAgent.id;
        }
      } else {
        agentId = existingAgent.id;
      }
      
      // Save output to ai_agent_outputs table
      const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
        user_id: user.id,
        agent_id: agentId,
        prompt: input,
        output_url: result,
        generation_type: 'text',
        llm_model: 'gpt-3.5-turbo',
        metadata: {},
        agent_type: 'text'
      });

      if (insertError) {
        console.error('Error saving output:', insertError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      toast.success('Output saved successfully');
      setSavedOutput({...savedOutput, saved: true});
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result || '');
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    try {
      // Strip HTML tags and decode HTML entities
      const stripHtml = (html: string): string => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
      };
      
      const cleanContent = stripHtml(result || '');
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 25;
      const contentWidth = pageWidth - (margin * 2);
      
      // Helper function for section headers
      const addSectionHeader = (text: string, yPos: number) => {
        // Add a subtle background and border for section headers
        pdf.setFillColor(245, 247, 250);
        pdf.setDrawColor(220, 225, 230);
        pdf.rect(margin - 5, yPos - 8, contentWidth + 10, 12, 'FD');
        
        // Make the text bold and slightly larger
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.setTextColor(45, 55, 72);
        pdf.text(text, margin, yPos);
        
        // Reset font settings for content
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        
        return yPos + 15;
      };
      
      // Add header with logo and title
      pdf.setFillColor(79, 70, 229);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Center align the main title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      const title = 'Project Methodology Recommendation';
      const titleWidth = pdf.getStringUnitWidth(title) * 24 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 28);
      
      // Reset text color
      pdf.setTextColor(0, 0, 0);
      
      // Add metadata
      let yPos = 50;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, margin, yPos);
      yPos += 20;
      
      // Project Details Section
      yPos = addSectionHeader('Project Details', yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      const wrappedInput = pdf.splitTextToSize(input, contentWidth);
      pdf.text(wrappedInput, margin, yPos + 8);
      yPos += wrappedInput.length * 7 + 15;
      
      // Recommendation Section
      const sections = cleanContent.split(/\d+\.\s+/).filter(Boolean);
      
      sections.forEach((section, index) => {
        // Check if we need a new page
        if (yPos > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          yPos = 20;
        }
        
        // Add section header
        yPos = addSectionHeader(`${index + 1}. ${section.split('\n')[0]}`, yPos);
        
        // Add section content
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.setTextColor(0, 0, 0);
        
        // Process bullet points and paragraphs
        const contentLines = section.split('\n').slice(1);
        let inSubsection = false;
        
        contentLines.forEach(line => {
          if (yPos > pdf.internal.pageSize.height - 20) {
            pdf.addPage();
            yPos = 20;
          }
          
          // Check if this is any kind of section title (ends with a colon)
          if (line.trim().match(/^[A-Za-z\s\&]+:$/)) {
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(45, 55, 72);
            const wrappedSubsection = pdf.splitTextToSize(line.trim(), contentWidth - 10);
            pdf.text(wrappedSubsection, margin, yPos + 8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(0, 0, 0);
            yPos += wrappedSubsection.length * 7 + 5;
            inSubsection = true;
            return;
          }
          
          const wrappedLine = pdf.splitTextToSize(line.trim(), contentWidth - 10);
          if (line.trim().startsWith('-')) {
            wrappedLine.forEach((l: string) => {
              pdf.text('•', margin + 5, yPos + 8);
              pdf.text(l.replace(/^-\s*/, ''), margin + 10, yPos + 8);
              yPos += 7;
            });
          } else if (line.trim()) {
            pdf.text(wrappedLine, margin + (inSubsection ? 5 : 0), yPos + 8);
            yPos += wrappedLine.length * 7;
          }
          yPos += 3;
          inSubsection = false;
        });
        yPos += 10;
      });
      
      // Add footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${i} of ${pageCount}`,
          pageWidth - margin - 20,
          pdf.internal.pageSize.height - 10
        );
      }

      pdf.save('methodology-recommendation.pdf');
    } catch (error) {
      console.error('Error downloading recommendation:', error);
      toast.error('Failed to download recommendation');
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Add header
      pdf.setFillColor(79, 70, 229);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Methodology Template', margin, 28);
      
      let yPos = 50;
      
      // Example Input section
      pdf.setTextColor(0, 0, 0);
      pdf.setFillColor(240, 244, 248);
      pdf.rect(margin, yPos - 6, contentWidth, 10, 'F');
      pdf.setFontSize(14);
      pdf.text('Example User Input', margin, yPos);
      
      yPos += 15;
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const exampleInput = "Hey, I'm building a health-tracking mobile app that uses AI to analyze users' fitness and diet patterns. The project is just starting, and we don't have everything fully planned. We want to launch an MVP in about 4-5 months. The team is small—just five developers, one designer, and one product manager. Our investors need updates every two weeks, and we might tweak features based on early user feedback. Also, we want to ensure compliance with health data regulations. What project management method should we use?";
      const wrappedExample = pdf.splitTextToSize(exampleInput, contentWidth);
      pdf.text(wrappedExample, margin, yPos);
      
      yPos += wrappedExample.length * 7 + 20;
      
      // Add new page for example response
      pdf.addPage();
      yPos = 20;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Example AI Response:', 20, yPos);
      
      const sections = [
        {
          title: '1. Project Analysis',
          content: [
            'Evolving requirements with MVP approach',
            'Need for frequent stakeholder updates',
            'Small, cross-functional team',
            'Regulatory compliance requirements'
          ]
        },
        {
          title: '2. Methodology Recommendation',
          content: [
            'Agile Scrum is ideal because:',
            '- Supports iterative development for MVP',
            '- 2-week sprints align with investor update needs',
            '- Facilitates user feedback integration',
            '- Small team size fits Scrum framework'
          ]
        },
        {
          title: '3. Implementation Steps',
          content: [
            'Set up a product backlog with core features',
            'Plan 2-week sprints with clear goals',
            'Hold daily stand-up meetings',
            'Conduct bi-weekly sprint reviews',
            'Integrate compliance checks into Definition of Done'
          ]
        }
      ];
      
      sections.forEach(section => {
        yPos += 20;
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(section.title, 20, yPos);
        
        pdf.setFont('helvetica', 'normal');
        section.content.forEach(line => {
          yPos += 10;
          pdf.text(line, 30, yPos);
        });
      });
      
      pdf.save('methodology-template.pdf');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const toggleFullResponse = () => {
    setShowFullResponse(!showFullResponse);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Project Methodology Advisor
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">Instructions</h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
              Describe your project details including scope, timeline, team size, stakeholder requirements, and any specific constraints. The AI will analyze your input and recommend the most suitable project management methodology.
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
            >
              <Download className="h-4 w-4 mr-1.5" />
              Download Template
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="project-details" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Project Details
              </label>
              <textarea
                id="project-details"
                rows={6}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Describe your project details..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Get Recommendation
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Methodology Recommendation
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    Copy
                  </button>
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleSaveOutput}
                    disabled={savingOutput || (savedOutput && savedOutput.saved)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingOutput ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-1.5" />
                        Saving...
                      </>
                    ) : savedOutput && savedOutput.saved ? (
                      'Saved'
                    ) : (
                      'Save Output'
                    )}
                  </button>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-4">
                <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                  Methodology Recommendation Generated Successfully
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}